import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Download, CalendarDays, Pencil, Syringe, Droplets, FlaskConical } from 'lucide-react';
import html2canvas from 'html2canvas';
import { Input } from './ui/input';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TIMES = ['Morning', 'Afternoon', 'Evening', 'Before bed'];
const STORE_KEY = 'alp_dosage_diary';
const VIAL_CHIPS = [2, 5, 10, 15, 20, 30, 50, 100];
const WATER_CHIPS = [1, 2, 3, 5];
const DOSE_CHIPS_MCG = [100, 250, 300, 500];
const DOSE_CHIPS_MG = [1, 2, 2.5, 5];

const parseMgFromItem = (item) => {
  if (!item) return '';
  const m = `${item.title} ${item.body || ''}`.match(/(\d+(?:\.\d+)?)\s*mg/i);
  return m ? m[1] : '';
};
const parseWaterFromItem = (item) => {
  if (!item) return '';
  const m = (item.bacWater || '').match(/(\d+(?:\.\d+)?)\s*ml/i);
  return m ? m[1] : '';
};

// Core maths: U-100 syringe -> 1mL = 100 units.
const calc = (vialMg, waterMl, doseVal, doseUnit) => {
  const v = parseFloat(vialMg); const w = parseFloat(waterMl); let d = parseFloat(doseVal);
  if (!v || !w || !d || v <= 0 || w <= 0 || d <= 0) return null;
  if (doseUnit === 'mcg') d = d / 1000;
  const units = (d / (v / w)) * 100;
  return {
    units,
    ml: d / (v / w),
    dosesPerVial: Math.floor(v / d),
    doseMg: d,
  };
};

const fmtUnits = (u) => (u >= 10 ? Math.round(u * 10) / 10 : Math.round(u * 100) / 100);

// Visual U-100 syringe with gold fill up to the draw mark.
const SyringeBar = ({ units }) => {
  const capped = Math.min(units, 100);
  return (
    <div className="mt-3" data-testid="diary-syringe-visual">
      <div className="relative h-7 rounded-full border border-alp-gold/40 bg-alp-dark overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-alp-gold/70 to-alp-gold rounded-l-full transition-all duration-500" style={{ width: `${capped}%` }} />
        {[10, 20, 30, 40, 50, 60, 70, 80, 90].map((t) => (
          <div key={t} className="absolute top-0 h-2 w-px bg-alp-cream/30" style={{ left: `${t}%` }} />
        ))}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[10px] tracking-[0.15em] uppercase font-semibold text-alp-black mix-blend-plus-lighter text-alp-cream drop-shadow">{fmtUnits(units)} units</span>
        </div>
      </div>
      <div className="flex justify-between text-[9px] text-alp-gray mt-1 px-0.5">
        <span>0</span><span>25</span><span>50</span><span>75</span><span>100 (1mL)</span>
      </div>
      {units > 100 && <p className="text-amber-300 text-[11px] mt-1">More than one full 1mL syringe &mdash; this draw needs {Math.ceil(units / 100)} fills or a larger syringe.</p>}
    </div>
  );
};

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? 'bg-alp-gold text-alp-black border-alp-gold font-semibold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60 hover:text-alp-cream'}`}>
    {children}
  </button>
);

const StepLabel = ({ n, icon: Icon, children }) => (
  <p className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2">
    <span className="w-5 h-5 rounded-full border border-alp-gold/50 flex items-center justify-center text-[10px]">{n}</span>
    <Icon size={13} /> {children}
  </p>
);

export const DosageDiary = ({ items = [] }) => {
  const gridRef = useRef(null);
  const [entries, setEntries] = useState(() => {
    try { const s = localStorage.getItem(STORE_KEY); return s ? JSON.parse(s) : []; } catch (e) { return []; }
  });
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ itemId: '', name: '', vialMg: '', waterMl: '', doseVal: '', doseUnit: 'mcg', time: 'Morning', notes: '', days: [] });
  const [peptideSearch, setPeptideSearch] = useState('');
  const [editingId, setEditingId] = useState('');

  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(entries)); } catch (e) {}
  }, [entries]);

  const setd = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const pickPeptide = (i) => setDraft((p) => ({
    ...p, itemId: i.id, name: i.title,
    vialMg: parseMgFromItem(i) || p.vialMg,
    waterMl: parseWaterFromItem(i) || p.waterMl,
  }));
  const toggleDay = (d) => setDraft((p) => ({ ...p, days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d] }));

  const preview = calc(draft.vialMg, draft.waterMl, draft.doseVal, draft.doseUnit);
  const doseText = draft.doseVal ? `${draft.doseVal}${draft.doseUnit}` : '';
  const canAdd = !!draft.name && !!preview && draft.days.length > 0;

  const addEntry = () => {
    if (!canAdd) return;
    setEntries((p) => [...p, {
      id: `${Date.now()}`, itemId: draft.itemId, name: draft.name,
      dose: doseText, doseVal: draft.doseVal, doseUnit: draft.doseUnit,
      vialMg: parseFloat(draft.vialMg), waterMl: parseFloat(draft.waterMl),
      units: preview.units, time: draft.time, notes: draft.notes, days: [...draft.days],
    }]);
    setDraft({ itemId: '', name: '', vialMg: '', waterMl: '', doseVal: '', doseUnit: 'mcg', time: 'Morning', notes: '', days: [] });
    setPeptideSearch('');
  };
  const remove = (id) => setEntries((p) => p.filter((e) => e.id !== id));
  const toggleEntryDay = (id, d) => setEntries((p) => p.map((e) => e.id === id ? { ...e, days: e.days.includes(d) ? e.days.filter((x) => x !== d) : [...e.days, d] } : e));
  const updateEntry = (id, patch) => setEntries((p) => p.map((e) => {
    if (e.id !== id) return e;
    const next = { ...e, ...patch };
    const c = calc(next.vialMg, next.waterMl, next.doseVal ?? next.dose, next.doseUnit || (String(next.dose || '').toLowerCase().includes('mcg') ? 'mcg' : 'mg'));
    next.units = c ? c.units : null;
    if (next.doseVal) next.dose = `${next.doseVal}${next.doseUnit || 'mcg'}`;
    return next;
  }));

  const todayKey = DAYS[(new Date().getDay() + 6) % 7];
  const todayEntries = entries.filter((e) => e.days.includes(todayKey));

  const GUIDE_CATS = ['Getting Started', 'Reconstitution'];
  const peptideItems = items.filter((i) => !GUIDE_CATS.includes(i.category || ''));
  const filteredItems = peptideSearch.trim()
    ? peptideItems.filter((i) => i.title.toLowerCase().includes(peptideSearch.trim().toLowerCase()))
    : peptideItems;

  const reconList = (() => {
    const seen = new Set(); const out = [];
    entries.forEach((e) => {
      if (e.vialMg && e.waterMl) {
        const key = `${e.name}|${e.vialMg}|${e.waterMl}`;
        if (!seen.has(key)) { seen.add(key); out.push(e); }
      }
    });
    return out;
  })();

  const saveImage = async () => {
    if (!gridRef.current) return;
    setSaving(true);
    try {
      const canvas = await html2canvas(gridRef.current, { backgroundColor: '#0A0A0A', scale: 2, useCORS: true, logging: false });
      await new Promise((resolve) => canvas.toBlob((blob) => {
        if (!blob) return resolve();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'alp-weekly-dosage-diary.png';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1500); resolve();
      }, 'image/png'));
    } catch (e) {} finally { setSaving(false); }
  };

  return (
    <div className="border border-alp-line rounded-lg bg-alp-card/40 p-6" data-testid="dosage-diary">
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays size={18} className="text-alp-gold" />
        <h3 className="font-serif text-2xl text-alp-cream">Weekly Dosage Diary</h3>
      </div>
      <p className="text-alp-gray text-xs mb-6">Pick a peptide, tell it your mix, and it works out exactly what to draw. Saved to this device. For research/educational tracking only.</p>

      {/* Today at a glance */}
      {todayEntries.length > 0 && (
        <div className="alp-beam rounded-xl p-[1.5px] mb-6" data-testid="diary-today-card">
          <div className="rounded-[calc(0.75rem-1.5px)] bg-alp-black/95 px-5 py-4">
            <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-2 flex items-center gap-2"><Syringe size={13} /> Today &middot; {todayKey}</p>
            <div className="space-y-1.5">
              {todayEntries.map((e) => (
                <div key={`today-${e.id}`} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
                  <span className="text-alp-cream text-sm">{e.name} <span className="text-alp-gray text-xs">&middot; {e.time}</span></span>
                  <span className="text-alp-gold font-serif text-lg" data-testid={`diary-today-units-${e.id}`}>{e.units != null ? `Draw ${fmtUnits(e.units)} units` : e.dose}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Guided add flow */}
      <div className="border border-alp-line/60 rounded-lg p-5 mb-6 bg-alp-dark/40">
        {/* Step 1: peptide */}
        <StepLabel n={1} icon={FlaskConical}>Pick your peptide</StepLabel>
        <Input value={peptideSearch} onChange={(e) => setPeptideSearch(e.target.value)} placeholder="Search peptides…" data-testid="diary-peptide-search" className="bg-alp-dark border-alp-line text-alp-cream h-9 mb-2 max-w-xs" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto pr-1" data-testid="diary-peptide-cards">
          {filteredItems.map((i) => (
            <button key={i.id} type="button" onClick={() => pickPeptide(i)} data-testid={`diary-peptide-card-${i.id}`}
              className={`text-left px-3 py-2 rounded-md border text-xs leading-tight transition-colors ${draft.itemId === i.id ? 'bg-alp-gold text-alp-black border-alp-gold font-semibold' : 'bg-alp-dark border-alp-line text-alp-cream hover:border-alp-gold/60'}`}>
              {i.title}
            </button>
          ))}
        </div>

        {/* Step 2: vial + water */}
        <div className="mt-5">
          <StepLabel n={2} icon={Droplets}>Your vial &amp; mix</StepLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Peptide in the vial (mg)</label>
              <Input type="number" inputMode="decimal" value={draft.vialMg} onChange={(e) => setd('vialMg', e.target.value)} data-testid="diary-vial-mg" placeholder="10" className="bg-alp-dark border-alp-line text-alp-cream h-10" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {VIAL_CHIPS.map((v) => <Chip key={v} active={String(draft.vialMg) === String(v)} onClick={() => setd('vialMg', String(v))} testid={`diary-vial-chip-${v}`}>{v}mg</Chip>)}
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">BAC water added (mL)</label>
              <Input type="number" inputMode="decimal" value={draft.waterMl} onChange={(e) => setd('waterMl', e.target.value)} data-testid="diary-water-ml" placeholder="2" className="bg-alp-dark border-alp-line text-alp-cream h-10" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {WATER_CHIPS.map((w) => <Chip key={w} active={String(draft.waterMl) === String(w)} onClick={() => setd('waterMl', String(w))} testid={`diary-water-chip-${w}`}>{w}mL</Chip>)}
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: dose */}
        <div className="mt-5">
          <StepLabel n={3} icon={Syringe}>Your dose</StepLabel>
          <div className="flex flex-wrap items-center gap-3">
            <Input type="number" inputMode="decimal" value={draft.doseVal} onChange={(e) => setd('doseVal', e.target.value)} data-testid="diary-dose" placeholder="250" className="bg-alp-dark border-alp-line text-alp-cream h-10 w-32" />
            <div className="flex rounded-full border border-alp-line overflow-hidden">
              {['mcg', 'mg'].map((u) => (
                <button key={u} type="button" onClick={() => setd('doseUnit', u)} data-testid={`diary-dose-unit-${u}`}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.1em] transition-colors ${draft.doseUnit === u ? 'bg-alp-gold text-alp-black font-semibold' : 'text-alp-gray hover:text-alp-cream'}`}>{u}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(draft.doseUnit === 'mcg' ? DOSE_CHIPS_MCG : DOSE_CHIPS_MG).map((d) => (
                <Chip key={d} active={String(draft.doseVal) === String(d)} onClick={() => setd('doseVal', String(d))} testid={`diary-dose-chip-${d}`}>{d}{draft.doseUnit}</Chip>
              ))}
            </div>
          </div>

          {/* LIVE units preview */}
          {preview ? (
            <div className="mt-4 border border-alp-gold/40 rounded-lg bg-alp-black/60 px-5 py-4" data-testid="diary-units-preview">
              <p className="font-serif text-2xl text-alp-gold">Draw {fmtUnits(preview.units)} units</p>
              <p className="text-alp-gray text-xs mt-0.5">= {preview.ml.toFixed(2)}mL on a U-100 insulin syringe &middot; ~{preview.dosesPerVial} dose{preview.dosesPerVial === 1 ? '' : 's'} per vial</p>
              <SyringeBar units={preview.units} />
            </div>
          ) : (
            <p className="text-alp-gray/70 text-xs mt-3" data-testid="diary-units-hint">Fill in vial size, BAC water and dose above and your exact draw appears here.</p>
          )}
        </div>

        {/* Step 4: schedule */}
        <div className="mt-5">
          <StepLabel n={4} icon={CalendarDays}>Days &amp; time</StepLabel>
          <div className="flex flex-wrap gap-2" data-testid="diary-days">
            {DAYS.map((d) => (
              <button key={d} type="button" onClick={() => toggleDay(d)} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.1em] border transition-colors ${draft.days.includes(d) ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60'}`}>{d}</button>
            ))}
            <button type="button" onClick={() => setd('days', draft.days.length === 7 ? [] : [...DAYS])} data-testid="diary-days-all" className="px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.1em] border border-alp-gold/40 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors">{draft.days.length === 7 ? 'Clear' : 'Every day'}</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Time</label>
              <select value={draft.time} onChange={(e) => setd('time', e.target.value)} className="w-full h-10 rounded-md bg-alp-dark border border-alp-line text-alp-cream text-sm px-3">
                {TIMES.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Notes (optional)</label>
              <Input value={draft.notes} onChange={(e) => setd('notes', e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream h-10" />
            </div>
          </div>
        </div>

        <button onClick={addEntry} disabled={!canAdd} data-testid="diary-add" className="mt-5 inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-50">
          <Plus size={14} /> Add to Diary
        </button>
        {!canAdd && draft.name && <span className="ml-3 text-alp-gray/70 text-[11px]">{!preview ? 'Complete steps 2-3 to work out your draw.' : 'Pick at least one day.'}</span>}
      </div>

      {/* Weekly grid */}
      <div ref={gridRef} className="bg-alp-black rounded-lg p-4">
        <p className="font-serif text-lg text-alp-gold mb-3 text-center">My Weekly Plan &middot; Australian Luxe</p>
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
          {DAYS.map((d) => {
            const dayEntries = entries.filter((e) => e.days.includes(d));
            const isToday = d === todayKey;
            return (
              <div key={d} className={`border rounded p-2 min-h-[90px] ${isToday ? 'border-alp-gold/60 bg-alp-gold/[0.04]' : 'border-alp-line'}`} data-testid={`diary-col-${d}`}>
                <p className={`text-[10px] tracking-[0.15em] uppercase mb-2 text-center ${isToday ? 'text-alp-gold' : 'text-alp-gray'}`}>{d}{isToday ? ' \u2022' : ''}</p>
                <div className="space-y-1.5">
                  {dayEntries.length === 0 ? <p className="text-alp-line text-[10px] text-center">—</p> : dayEntries.map((e) => (
                    <div key={e.id} className="bg-alp-card/60 rounded px-2 py-1.5 border border-alp-gold/20">
                      <p className="text-alp-cream text-[11px] leading-tight font-medium">{e.name}</p>
                      {e.units != null ? <p className="text-alp-gold text-[12px] font-semibold">{fmtUnits(e.units)} units</p> : null}
                      {e.dose ? <p className="text-alp-gray text-[10px]">{e.dose} &middot; {e.time}</p> : null}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {reconList.length > 0 && (
          <div className="mt-4 pt-4 border-t border-alp-line/60" data-testid="diary-reference-legend">
            <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2">Reconstitution Reference</p>
            <div className="border border-alp-gold/25 rounded-lg bg-alp-dark/50 divide-y divide-alp-line/40">
              {reconList.map((e) => (
                <div key={`recon-${e.id}`} data-testid={`diary-recon-${e.id}`} className="flex items-center justify-between px-4 py-2.5 gap-3">
                  <span className="text-alp-cream text-sm font-medium">{e.name}</span>
                  <span className="text-alp-gold text-xs whitespace-nowrap">{e.vialMg}mg vial + {e.waterMl}mL BAC water</span>
                </div>
              ))}
            </div>
            <p className="text-alp-gray/70 text-[10px] mt-2">Units shown on a standard U-100 insulin syringe (100 units = 1mL). Research use only.</p>
          </div>
        )}
      </div>

      {/* Entry list with actions */}
      {entries.length > 0 && (
        <div className="mt-6 space-y-2">
          {entries.map((e) => (
            <div key={e.id} data-testid={`diary-entry-${e.id}`} className="border border-alp-line/60 rounded p-3 bg-alp-dark/40">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-alp-cream text-sm font-medium truncate">{e.name} {e.units != null ? <span className="text-alp-gold">&middot; draw {fmtUnits(e.units)} units</span> : null} {e.dose ? <span className="text-alp-gray">({e.dose})</span> : null}</p>
                  <p className="text-alp-gray text-xs truncate">{e.days.length ? e.days.join(', ') : 'No days set'} · {e.time}{e.notes ? ` · ${e.notes}` : ''}</p>
                </div>
                <button onClick={() => setEditingId((c) => (c === e.id ? '' : e.id))} data-testid={`diary-edit-${e.id}`} className={`shrink-0 inline-flex items-center gap-1.5 border px-3 py-1.5 rounded text-[10px] tracking-[0.12em] uppercase transition-colors ${editingId === e.id ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black'}`}>
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => remove(e.id)} data-testid={`diary-remove-${e.id}`} className="shrink-0 text-alp-gray hover:text-red-400 p-1"><Trash2 size={15} /></button>
              </div>
              {editingId === e.id && (
                <div className="mt-3 border-t border-alp-line/60 pt-3" data-testid={`diary-editor-${e.id}`}>
                  <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Days</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {DAYS.map((d) => (
                      <button key={d} type="button" onClick={() => toggleEntryDay(e.id, d)} data-testid={`diary-edit-day-${e.id}-${d}`} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.1em] border transition-colors ${e.days.includes(d) ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60'}`}>{d}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Vial (mg)</label>
                      <Input type="number" value={e.vialMg || ''} onChange={(ev) => updateEntry(e.id, { vialMg: ev.target.value })} data-testid={`diary-edit-vial-${e.id}`} className="bg-alp-dark border-alp-line text-alp-cream h-10" />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Water (mL)</label>
                      <Input type="number" value={e.waterMl || ''} onChange={(ev) => updateEntry(e.id, { waterMl: ev.target.value })} data-testid={`diary-edit-water-${e.id}`} className="bg-alp-dark border-alp-line text-alp-cream h-10" />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Dose ({e.doseUnit || 'mcg'})</label>
                      <Input type="number" value={e.doseVal || ''} onChange={(ev) => updateEntry(e.id, { doseVal: ev.target.value })} data-testid={`diary-edit-dose-${e.id}`} className="bg-alp-dark border-alp-line text-alp-cream h-10" />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Time</label>
                      <select value={e.time} onChange={(ev) => updateEntry(e.id, { time: ev.target.value })} data-testid={`diary-edit-time-${e.id}`} className="w-full h-10 rounded-md bg-alp-dark border border-alp-line text-alp-cream text-sm px-3">
                        {TIMES.map((t) => (<option key={t} value={t}>{t}</option>))}
                      </select>
                    </div>
                  </div>
                  {e.units != null && <p className="text-alp-gold text-sm mt-2">Draw {fmtUnits(e.units)} units</p>}
                  <button onClick={() => setEditingId('')} data-testid={`diary-edit-done-${e.id}`} className="mt-3 inline-flex items-center gap-2 bg-alp-gold text-alp-black px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Done</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {entries.length > 0 && (
        <button onClick={saveImage} disabled={saving} data-testid="diary-save-image" className="mt-6 inline-flex items-center gap-2 border border-alp-gold/50 text-alp-gold px-6 py-3 text-xs tracking-[0.18em] uppercase font-semibold hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
          <Download size={15} /> {saving ? 'Saving…' : 'Save Diary as Image'}
        </button>
      )}
    </div>
  );
};
